/**
 * One-shot location capture for a clock-in/out. Location is only read while the
 * scan screen is open — never in the background (see PLAN.md §8).
 *
 * We use watchPosition rather than getCurrentPosition because the first fix indoors
 * is often poor; watching lets us keep the most accurate fix that arrives within
 * MAX_WATCH_MS and stop as soon as one is good enough.
 */

export interface GeoFix {
  lat: number;
  lng: number;
  accuracy: number;
}

export interface GeoSnapshot {
  /** Most accurate fix seen so far. */
  fix: GeoFix | null;
  /** Last GeolocationPositionError code (1 denied, 2 unavailable, 3 timeout), if any. */
  errorCode: number | null;
  /** True once watching has stopped (good fix, time limit, denied or stopped). */
  finished: boolean;
}

/** Stop watching once a fix is at least this accurate. */
export const GOOD_ACCURACY_M = 25;
/** Give up improving the fix after this long. */
export const MAX_WATCH_MS = 20_000;
/** A fix this accurate is good enough to send straight away after a scan. */
export const SEND_NOW_ACCURACY_M = 50;
/** The server treats anything worse than this as "location unavailable". */
export const POOR_ACCURACY_M = 150;

export const GEO_DENIED = 1;
export const GEO_UNAVAILABLE = 2;
export const GEO_TIMEOUT = 3;

type Listener = (s: GeoSnapshot) => void;

export class LocationCapture {
  private watchId: number | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private deadline = 0;
  private listeners = new Set<Listener>();
  snapshot: GeoSnapshot = { fix: null, errorCode: null, finished: false };

  /** Starts watching. Safe to call more than once. */
  start(): void {
    if (this.watchId !== null || this.snapshot.finished) return;
    this.watch(MAX_WATCH_MS);
  }

  /**
   * Makes sure we keep watching for at least `ms` more, keeping the best fix so far.
   * Restarts a capture that has already finished (time limit, timeout, or permission
   * denied — the cleaner may have just allowed it in Settings). Does nothing once we
   * have a fix good enough to stop on.
   */
  keepWatching(ms: number = MAX_WATCH_MS): void {
    const fix = this.snapshot.fix;
    if (fix && fix.accuracy <= GOOD_ACCURACY_M) return;
    if (this.watchId !== null) {
      // Still watching: push the deadline back if it would come too soon.
      const remaining = this.deadline - Date.now();
      if (remaining >= ms) return;
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => this.stop(), ms);
      this.deadline = Date.now() + ms;
      return;
    }
    // Forget the old error (it may no longer apply), but keep the best fix.
    this.update({ finished: false, errorCode: null });
    this.watch(ms);
  }

  private watch(ms: number): void {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      this.update({ errorCode: GEO_UNAVAILABLE, finished: true });
      return;
    }
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this.onPosition(pos),
      (err) => this.onError(err),
      // A generous per-fix timeout: our own timer decides when to give up.
      { enableHighAccuracy: true, maximumAge: 0, timeout: Math.max(ms, MAX_WATCH_MS) },
    );
    this.timer = setTimeout(() => this.stop(), ms);
    this.deadline = Date.now() + ms;
  }

  /** Stops watching and keeps whatever fix we have. */
  stop(): void {
    if (this.watchId !== null) navigator.geolocation.clearWatch(this.watchId);
    this.watchId = null;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (!this.snapshot.finished) this.update({ finished: true });
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.snapshot);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Resolves when capture finishes, a fix of `goodEnoughM` or better is available,
   * or `maxMs` passes — whichever comes first. Always resolves with the best so far.
   */
  waitForFix(maxMs: number, goodEnoughM = SEND_NOW_ACCURACY_M): Promise<GeoSnapshot> {
    return new Promise((resolve) => {
      let unsubscribe = () => {};
      const done = () => {
        clearTimeout(timer);
        unsubscribe();
        resolve(this.snapshot);
      };
      const timer = setTimeout(done, maxMs);
      unsubscribe = this.subscribe((s) => {
        if (s.finished || (s.fix && s.fix.accuracy <= goodEnoughM)) queueMicrotask(done);
      });
    });
  }

  private onPosition(pos: GeolocationPosition) {
    const { latitude, longitude, accuracy } = pos.coords;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || !Number.isFinite(accuracy)) return;
    const best = this.snapshot.fix;
    if (!best || accuracy < best.accuracy) {
      this.update({ fix: { lat: latitude, lng: longitude, accuracy }, errorCode: null });
    }
    if (accuracy <= GOOD_ACCURACY_M) this.stop();
  }

  private onError(err: GeolocationPositionError) {
    // Keep a good fix if we already have one; just remember the error otherwise.
    if (!this.snapshot.fix) this.update({ errorCode: err.code });
    // Denied and timed out are final; "unavailable" can recover while we keep watching.
    if (err.code === GEO_DENIED || err.code === GEO_TIMEOUT) this.stop();
  }

  private update(patch: Partial<GeoSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const l of this.listeners) l(this.snapshot);
  }
}

/** Payload fields for PunchRequest from a capture snapshot. */
export function punchLocation(s: GeoSnapshot): {
  fix: GeoFix | null;
  geoError: number | null;
} {
  if (s.fix) return { fix: s.fix, geoError: null };
  return { fix: null, geoError: s.errorCode ?? GEO_TIMEOUT };
}

/** Current permission state where the browser exposes it (Safari may not). */
export async function locationPermission(): Promise<PermissionState | "unknown"> {
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state;
  } catch {
    return "unknown";
  }
}

// --- Location allowed before? ------------------------------------------------
// Set after this phone has given us a location once. While it's set, the clock flow
// skips the "Allow location" step; it's cleared if location is later refused.

const ALLOWED_KEY = "clockin.locationAllowed";

export function locationAllowedBefore(): boolean {
  try {
    return localStorage.getItem(ALLOWED_KEY) === "1";
  } catch {
    return false;
  }
}

export function rememberLocationAllowed(allowed: boolean): void {
  try {
    if (allowed) localStorage.setItem(ALLOWED_KEY, "1");
    else localStorage.removeItem(ALLOWED_KEY);
  } catch {
    // Storage blocked: we'll simply show the "Allow location" step again next time.
  }
}

// --- Privacy notice acknowledgement ---------------------------------------

const NOTICE_KEY = "clockin.locationNoticeAck";

export function locationNoticeAcknowledged(): boolean {
  try {
    return localStorage.getItem(NOTICE_KEY) === "1";
  } catch {
    return false;
  }
}

export function acknowledgeLocationNotice(): void {
  try {
    localStorage.setItem(NOTICE_KEY, "1");
  } catch {
    // Storage blocked: we'll simply show the notice again next time.
  }
}
